import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getApiBaseUrl } from '../config';

// Resolve initial tenant ID from multiple sources:
// 1. Subdomain (e.g., acme.commander.app -> look up tenant by slug)
// 2. Previously persisted tenant ID (from localStorage via zustand persist)
// 3. Fallback to dev default for local development
function resolveDefaultTenantId(): string {
  // Try subdomain-based resolution
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Skip localhost and IP addresses
    if (hostname !== 'localhost' && !hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
      const parts = hostname.split('.');
      // If subdomain exists (e.g., acme.commander.app has 3+ parts)
      if (parts.length >= 3) {
        const subdomain = parts[0];
        // Store the slug; the login flow will resolve it to a tenant ID
        // For now, return the subdomain as a hint (the API will resolve it)
        console.log(`[Auth] Detected tenant subdomain: ${subdomain}`);
      }
    }
  }

  // Default for development — will be overridden by login response
  return '00000000-0000-0000-0000-000000000001';
}

const DEFAULT_TENANT_ID = resolveDefaultTenantId();

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  tier?: string;
}

export interface User {
  id: string;
  email: string;
  username?: string;
  firstName: string;
  lastName: string;
  roles: string[];
  permissions: string[];
  modules: string[];
  tenantId: string;
  /** The organization this user is rooted in. Pages that pick org/site
   *  use this (plus the org tree's `path` materialized hierarchy) to
   *  narrow choices to the user's subtree. `null` or absent for
   *  super-admins and tenant-wide service accounts — those see all
   *  organizations. */
  organizationId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  tenantId: string;
  tenantInfo: TenantInfo | null;
  availableTenants: TenantInfo[];
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setTenantId: (tenantId: string) => void;
  setTenantInfo: (info: TenantInfo) => void;
  setAvailableTenants: (tenants: TenantInfo[]) => void;
  switchTenant: (tenantId: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasModule: (moduleId: string) => boolean;
  isSuperAdmin: () => boolean;
  getAuthHeaders: () => Record<string, string>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      tenantId: DEFAULT_TENANT_ID,
      tenantInfo: null,
      availableTenants: [],
      isAuthenticated: false,
      isLoading: false,

      setUser: (user) => set({ user, isAuthenticated: true }),

      setTokens: (accessToken, refreshToken) => {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        set({ accessToken, refreshToken });
      },

      setTenantId: (tenantId) => set({ tenantId }),

      setTenantInfo: (info) => set({ tenantInfo: info, tenantId: info.id }),

      setAvailableTenants: (tenants) => set({ availableTenants: tenants }),

      switchTenant: async (tenantId: string) => {
        const { availableTenants } = get();
        const target = availableTenants.find((t) => t.id === tenantId);

        if (!target) {
          throw new Error('Tenant not available for switching');
        }

        // Update tenant context — user must re-login or refresh to get tenant-scoped data
        set({
          tenantId: target.id,
          tenantInfo: target,
        });

        // Force a page reload to re-initialize all data with the new tenant context
        window.location.reload();
      },

      getAuthHeaders: () => {
        const { accessToken, tenantId } = get();
        const headers: Record<string, string> = {
          'X-Tenant-ID': tenantId,
        };
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return headers;
      },

      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const { tenantId } = get();
          const apiUrl = getApiBaseUrl();
          console.log('[Auth] Login API URL:', apiUrl, 'isTauri:', typeof window !== 'undefined' && '__TAURI__' in window);
          const response = await fetch(`${apiUrl}/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-ID': tenantId,
            },
            body: JSON.stringify({ email, password }),
          });

          if (!response.ok) {
            let errorMessage = 'Login failed';
            try {
              const error = await response.json();
              errorMessage = error.error?.message || error.message || 'Login failed';
            } catch {
              // Response body is not valid JSON
              errorMessage = `Login failed: ${response.status} ${response.statusText}`;
            }
            throw new Error(errorMessage);
          }

          const text = await response.text();
          if (!text) {
            throw new Error('Empty response from server');
          }

          let data;
          try {
            data = JSON.parse(text);
          } catch {
            throw new Error('Invalid JSON response from server');
          }
          const { user, accessToken, refreshToken } = data.data;

          // Extract tenant info from login response if provided
          const responseTenantId = data.data.tenantId || user.tenantId || tenantId;
          const responseTenantInfo: TenantInfo | null = data.data.tenant
            ? {
                id: data.data.tenant.id || responseTenantId,
                name: data.data.tenant.name || '',
                slug: data.data.tenant.slug || '',
                tier: data.data.tenant.tier,
              }
            : null;

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);

          set({
            user: { ...user, modules: user.modules || [], tenantId: responseTenantId },
            accessToken,
            refreshToken,
            tenantId: responseTenantId,
            tenantInfo: responseTenantInfo,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        // Clear all commander-related localStorage keys
        const keysToRemove = Object.keys(localStorage).filter(k =>
          k.startsWith('commander') || k === 'auth-storage' ||
          k === 'accessToken' || k === 'refreshToken' ||
          k.startsWith('floorPlan') || k.startsWith('deviceLibrary') ||
          k.startsWith('settings-') || k.startsWith('hmi-')
        );
        keysToRemove.forEach(k => localStorage.removeItem(k));

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          tenantInfo: null,
          availableTenants: [],
        });

        // Call logout endpoint to clear HttpOnly cookies
        fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
      },

      refreshAuth: async () => {
        const { refreshToken, tenantId } = get();
        if (!refreshToken) {
          get().logout();
          return;
        }

        try {
          const response = await fetch(`${getApiBaseUrl()}/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-ID': tenantId,
            },
            body: JSON.stringify({ refreshToken }),
          });

          if (!response.ok) {
            get().logout();
            return;
          }

          const text = await response.text();
          if (!text) {
            get().logout();
            return;
          }

          let data;
          try {
            data = JSON.parse(text);
          } catch {
            get().logout();
            return;
          }
          const { accessToken: newAccessToken, refreshToken: newRefreshToken } = data.data;

          get().setTokens(newAccessToken, newRefreshToken);
        } catch {
          get().logout();
        }
      },

      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;
        // Wildcard grants all permissions
        if (user.permissions?.includes('*')) return true;
        // Admin roles have all permissions
        const isAdmin = user.roles?.some(r =>
          r === 'admin' || r === 'super_admin' || r === 'Admin' || r === 'SuperAdmin'
        );
        if (isAdmin) return true;
        // Check explicit permissions (includes group-aggregated permissions from login)
        return user.permissions?.includes(permission) ?? false;
      },

      hasRole: (role) => {
        const { user } = get();
        if (!user) return false;
        return user.roles?.includes(role) ?? false;
      },

      hasModule: (moduleId) => {
        const { user } = get();
        if (!user) return false;
        // Admin roles bypass module restrictions
        const isAdmin = user.roles?.some(r =>
          r === 'admin' || r === 'super_admin' || r === 'Admin' || r === 'SuperAdmin'
        );
        if (isAdmin) return true;
        // Wildcard grants all modules
        if (user.modules?.includes('*')) return true;
        // No modules assigned = no access (secure by default)
        if (!user.modules || user.modules.length === 0) return false;
        return user.modules.includes(moduleId);
      },

      isSuperAdmin: () => {
        const { user } = get();
        if (!user) return false;
        return (
          user.roles?.includes('super_admin') ||
          user.roles?.includes('SuperAdmin') ||
          user.permissions?.includes('*') ||
          false
        );
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        tenantId: state.tenantId,
        tenantInfo: state.tenantInfo,
        availableTenants: state.availableTenants,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
